import { useRef, useState } from "react";
import { Autocomplete } from "@react-google-maps/api";
import { AddressComponents } from "./AddressComponents";

interface GoogleSearchBoxProps {
  onPlaceSelected: (address: AddressComponents) => void;
  initialValue?: string;
  onInputChange?: (value: string) => void;
}

export default function GoogleSearchBox({
  onPlaceSelected,
  initialValue = "",
  onInputChange,
}: GoogleSearchBoxProps) {
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const onPlaceChanged = () => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (!place?.address_components || !place.geometry?.location) return;

    const address: AddressComponents = {
      fullAddress: place.formatted_address,
      location: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      },
    };

    place.address_components.forEach((component) => {
      if (
        component.types.includes("street_number") ||
        component.types.includes("route")
      ) {
        address.street = address.street
          ? `${address.street} ${component.long_name}`
          : component.long_name;
      }
      if (component.types.includes("locality")) {
        address.city = component.long_name;
      }
      if (component.types.includes("administrative_area_level_1")) {
        address.state = component.long_name;
      }
      if (component.types.includes("country")) {
        address.country = component.long_name;
      }
      if (component.types.includes("postal_code")) {
        address.postalCode = component.long_name;
      }
    });

    onPlaceSelected(address);
  };

  return (
    <Autocomplete
      onLoad={(ac) => setAutocomplete(ac)}
      onPlaceChanged={onPlaceChanged}
      options={{
        componentRestrictions: { country: "au" },
        types: ["geocode"],
        fields: ["address_components", "formatted_address", "geometry.location"],
      }}
    >
      <div className="flex flex-col w-100 gap-1 px-4 p-4 border-gray-500 rounded-2xl hover:bg-gray-100 transition-colors cursor-pointer">
        <input
          ref={inputRef}
          type="text"
          placeholder="Search suburb, postcode or state"
          defaultValue={initialValue}
          className="w-full focus:outline-none bg-transparent placeholder-gray-500 text-sm"
          id="location-search"
          onChange={(e) => onInputChange?.(e.target.value)}
        />
      </div>
    </Autocomplete>
  );
}
